import apiClient from './apiClient';
import config from '../config/appConfig';
import { logger } from '../services/logger';

export interface OnboardProfileRequest {
    email: string;
    firstName: string;
    lastName: string;
    companyName: string;
    ownerId: string;
    ownerEmail: string;
    environments: {
        name: string;
        types: { type: string }[];
    }[];
}

export interface UserProfileResponse {
    [key: string]: any;
}

/**
 * Get user profile.
 * GET /user-profile/profiles?email=...
 * Returns profile data if exists (200), null if not found (404).
 */
export const getUserProfile = async (email: string): Promise<UserProfileResponse | null> => {
    try {
        const response = await apiClient.get(`/user-profile/profiles`, { params: { email } });
        return response.data || response;
    } catch (error: any) {
        if (error?.statusCode === 404 || error?.code === 'ResourceNotFound' || error?.response?.status === 404) {
            return null;
        }
        throw error;
    }
};

/**
 * Register user session after login.
 * POST /appmgr/users/self
 * Called for both sandbox and production environments.
 * Errors are logged for tracking but not shown in UI.
 */
export const registerUserSession = async (): Promise<void> => {
    const sandboxUrl = config.apiSandboxUrl.replace(/\/$/, '');
    const prodUrl = config.apiProductionUrl.replace(/\/$/, '');

    const callSelf = async (baseURL: string, env: string) => {
        try {
            await apiClient.post(`/appmgr/users/self`, {}, { baseURL });
        } catch (error: any) {
            logger.trackError(
                error instanceof Error ? error : new Error(String(error?.message || error)),
                `registerUserSession.${env}`
            );
        }
    };

    await Promise.allSettled([
        callSelf(sandboxUrl, 'sandbox'),
        callSelf(prodUrl, 'production'),
    ]);
};

/**
 * Create/onboard a user profile.
 * Called when GET profile returns 404 for both sandbox and production.
 * Backend calls MPA to create merchant and returns the profile object.
 * 
 * PUT /user-profile/profiles/:userid
 */
export const createUserProfile = async (userId: string, payload: OnboardProfileRequest) => {
    return apiClient.put(`/user-profile/profiles/${userId}`, payload);
};
